import React from 'react';
import Client from '../Client';
import {
  Card,
  Box,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  SvgIcon,
  Typography,
  TextField,
  MenuItem
} from '@mui/material';
import Grid from '@mui/material/Grid';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import OverviewMonthSales from './OverviewMonthSales';
import OverviewTopSales from './OverviewTopSales';
import OverviewDailySalesChart from './OverviewDailySalesChart';
import {
  locationsList,
  salesSimpleBiSummary,
  salesTargetAttainment
} from './simpleBiReportsApi';
import { format } from 'date-fns';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function datediff(first, second) {
  return Math.round((second - first) / (1000 * 60 * 60 * 24));
}

function parseDate(str) {
  var mdy = str.split('-');
  return new Date(mdy[0], mdy[1] - 1, mdy[2]);
}

let d = new Date();
let yesterday = format(new Date().setDate(new Date().getDate() - 1), 'yyyy-MM-dd');
let dzMonth = format(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), 'yyyy-MM-dd');
let countToDZ = datediff(parseDate(yesterday), parseDate(dzMonth));

class OverviewSales extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),
      sales_total: [],
      target_total: [],
      location: 0,
      locationName: { name: "All Locations" },
      locations: []
    };

    this.getData = this.getData.bind(this);
    this.doSync = this.doSync.bind(this);
    this.selectLocation = this.selectLocation.bind(this);
  }

  componentDidMount() {
    this.getLocations();
    this.getData();
  }

  getLocations() {
    let locations = locationsList(this.state.client);

    if (locations && Array.isArray(locations.locations)) {
      const locs = locations.locations;
      if (locs.length === 1) {
        this.setState(
          {
            locations: locs,
            location: locs[0].iref,
            locationName: locs[0]
          },
          () => this.getData()
        );
      } else {
        this.setState({ locations: locs });
      }
    }
  }

  getAvailableLocations() {
    let locations = [];
    if (this.state.locations) {
      Object.keys(this.state.locations).forEach(key => {
        let fData = this.state.locations[key];
        if (fData && fData.iref !== undefined) {
          locations.push({
            iref: fData.iref,
            name: fData.name
          });
        }
      });
    }
    return locations;
  }

  getData() {
    let querySalesData = {
      location: this.state.location
    };

    let queryTargetData = {
      location: this.state.location
    };

    let salesData = salesSimpleBiSummary(this.state.client, querySalesData);
    if (salesData) {
      this.setState({ sales_total: salesData });
    }

    let targetData = salesTargetAttainment(this.state.client, queryTargetData);
    if (targetData) {
      this.setState({ target_total: targetData });
    }

  }

  doSync() {
    this.getData();
  }

  selectLocation = (event) => {
    const value = event.target.value;

    const locationObj = this.state.locations.find(
      (o) => String(o.iref) === String(value)
    );

    this.setState(
      {
        location: value,
        locationName: locationObj || { name: "All Locations" }
      },
      () => {
        this.doSync();
      }
    );
  };

  formatAmount(value) {
    const numberValue = parseInt(value || 0);

    if (numberValue > 1000000) {
      return thousandsSeparator((numberValue / 1000000).toFixed(2)) + " M";
    }

    return thousandsSeparator(numberValue.toFixed(0));
  }

  renderKpiCard(title, mainValue, footerValue, footerLabel, positive) {
    return (
      <Box
        sx={{
          background: "#f6f4ef",
          borderRadius: "14px",
          p: {
            xs: 1.2,
            sm: 1.5
          },
          minHeight: {
            xs: 112,
            sm: 116,
            md: 112
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        }}
      >
        <Stack alignItems="center" justifyContent="center" spacing={1}>
          <Typography
            color="#1a237e"
            align="center"
            sx={{
              lineHeight: 1.2,
              fontSize: {
                xs: "11px",
                sm: "12px"
              },
              fontWeight: 800,
              letterSpacing: "0.5px",
              textTransform: "uppercase"
            }}
          >
            {title}
          </Typography>

          <Typography
            align="center"
            sx={{
              color: "#1e2833",
              fontWeight: 800,
              fontSize: {
                xs: "22px",
                sm: "26px",
                md: "28px"
              },
              lineHeight: 1.1
            }}
          >
            {mainValue}
          </Typography>
        </Stack>

        <Stack
          alignItems="center"
          justifyContent="center"
          direction="row"
          spacing={0.75}
          sx={{ mt: 1.25 }}
        >
          <SvgIcon color={positive ? 'success' : 'error'} fontSize="small">
            {positive ? <EmojiEventsIcon /> : <DirectionsRunIcon />}
          </SvgIcon>

          <Typography
            color={positive ? 'success.main' : 'error.main'}
            sx={{
              fontSize: {
                xs: "14px",
                sm: "16px"
              },
              fontWeight: 700,
              lineHeight: 1
            }}
          >
            {footerValue}
          </Typography>

          <Typography
            color="text.secondary"
            sx={{
              fontSize: {
                xs: "11px",
                sm: "12px"
              },
              lineHeight: 1
            }}
          >
            {footerLabel}
          </Typography>
        </Stack>
      </Box>
    );
  }

  render() {
    let salesToday = 0;
    let salesYesterday = 0;
    let salesMTD = 0;
    let salesLM = 0;
    let salesLMTD = 0;

    let targetToday = 0;
    let targetYesterday = 0;
    let targetMTD = 0;
    let targetTM = 0;
    let targetLM = 0;

    let targetDiff = 0;
    let leftTarget = 0;
    let location = "";

    let kpiList = [];

    if (this.state.target_total) {
      let target_total = this.state.target_total;

      if (target_total.target_Today !== undefined) targetToday = parseInt(target_total.target_Today);
      if (target_total.target_Yesterday !== undefined) targetYesterday = parseInt(target_total.target_Yesterday);
      if (target_total.target_MTD !== undefined) targetMTD = parseInt(target_total.target_MTD);
      if (target_total.target_TM !== undefined) targetTM = parseInt(target_total.target_TM);
      if (target_total.target_LM !== undefined) targetLM = parseInt(target_total.target_LM);
      if (target_total.sales_LM !== undefined) salesLM = parseInt(target_total.sales_LM);
    }

    if (this.state.sales_total) {
      let sales_total = this.state.sales_total;

      if (sales_total.sales_Today !== undefined) salesToday = parseInt(sales_total.sales_Today);
      if (sales_total.sales_Yesterday !== undefined) salesYesterday = parseInt(sales_total.sales_Yesterday);
      if (sales_total.sales_TM !== undefined) salesMTD = parseInt(sales_total.sales_TM);
      if (sales_total.sales_LM !== undefined) salesLMTD = parseInt(sales_total.sales_LM);
      if ((salesMTD - salesToday) > 0) salesMTD = parseInt(salesMTD - salesToday);
      if (sales_total.location !== undefined) location = sales_total.location.toUpperCase();
    }

    if ((targetMTD - salesMTD) / countToDZ > 0) {
      targetDiff = (targetMTD - salesMTD) / countToDZ;
    }

    if ((targetTM - salesMTD) > 0) {
      leftTarget = (targetTM - salesMTD);
    }

    kpiList.push({ period: "Today", sales: salesToday, target: targetToday });
    kpiList.push({ period: "Yesterday", sales: salesYesterday, target: targetYesterday });
    kpiList.push({ period: "This Month", sales: salesMTD, target: targetMTD });
    kpiList.push({ period: "Last Month", sales: salesLM, target: targetLM });

    const targetReachedPercent = targetMTD > 0
      ? parseFloat(salesMTD / targetMTD * 100).toFixed(1)
      : parseFloat(0).toFixed(1);

    const availableLocations = this.getAvailableLocations();
    const showLocationSelect = availableLocations.length > 1;
    let locations = showLocationSelect
      ? [{ iref: 0, name: "All Locations" }, ...availableLocations]
      : availableLocations;
    const singleLocationName = availableLocations[0]?.name || this.state.locationName?.name || "Location";

    return (
      <Card
        sx={{
          overflow: 'hidden',
          position: 'relative',
          borderRadius: "14px",
          height: {
            xs: "calc(100vh - 92px)",
            md: "calc(100vh - 104px)"
          },
          minHeight: {
            xs: 500,
            sm: 540
          },
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Location filter */}
        <Box
          sx={{
            px: {
              xs: 1,
              sm: 1.5
            },
            py: {
              xs: 0.75,
              sm: 1
            },
            display: "flex",
            alignItems: "end",
            gap: 1,
            borderBottom: "1px solid rgba(0,0,0,0.08)"
          }}
        >
          {showLocationSelect ? (
  <TextField
    select
    label="Location"
    value={this.state.location}
    variant="standard"
    size="small"
    sx={{
      width: {
        xs: "100%",
        sm: 220
      },
      "& .MuiInputBase-input": {
        fontSize: {
          xs: "14px",
          sm: "14px"
        }
      },
      "& .MuiInputLabel-root": {
        fontSize: {
          xs: "13px",
          sm: "13px"
        }
      }
    }}
    onChange={this.selectLocation}
  >
    {locations.map((option) => (
      <MenuItem key={option.iref} value={option.iref}>
        {option.name}
      </MenuItem>
    ))}
  </TextField>
            ) : (
              <Box>
                <Typography
                  sx={{
                    color: "#1a237e",
                    fontSize: {
                      xs: "11px",
                      sm: "12px"
                    },
                    fontWeight: 800,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    lineHeight: 1.2
                  }}
                >
                  Location
                </Typography>

                <Typography
                  sx={{
                    color: "#1e2833",
                    fontSize: {
                      xs: "15px",
                      sm: "16px"
                    },
                    fontWeight: 800,
                    lineHeight: 1.3,
                    mt: 0.25
                  }}
                >
                  {singleLocationName}
                </Typography>
              </Box>
            )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            p: {
              xs: 1,
              sm: 1.25
            }
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={6}>
              {this.renderKpiCard(
                "Total Target",
                this.formatAmount(targetTM),
                targetReachedPercent + "%",
                "target",
                targetMTD < salesMTD
              )}
            </Grid>

            <Grid item xs={6}>
              {this.renderKpiCard(
                "Target To Go",
                this.formatAmount(leftTarget),
                this.formatAmount(targetToday + targetDiff),
                "today",
                true
              )}
            </Grid>

            <Grid item xs={6}>
              <OverviewMonthSales location={this.state.location} />
            </Grid>

            <Grid item xs={6}>
              <OverviewTopSales location={this.state.location} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: "14px",
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.08)",
                  mt: 0.25,
                  height: "100%"
                }}
              >
                <TableContainer>
                  <Table stickyHeader aria-label="overview table" size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={this.headerCellSx("left")}>
                          <b>Total Value</b>
                        </TableCell>

                        <TableCell sx={this.headerCellSx("right")}>
                          <b>Sales</b>
                        </TableCell>

                        <TableCell sx={this.headerCellSx("right")}>
                          <b>Target</b>
                        </TableCell>

                        <TableCell sx={this.headerCellSx("center")}>
                          <b>%</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {kpiList.map((kpi) => {
                        return (
                          <TableRow key={kpi.period}>
                            <TableCell sx={this.bodyCellSx("left", true)}>
                              {kpi.period}
                            </TableCell>

                            <TableCell sx={this.bodyCellSx("right")}>
                              {thousandsSeparator(parseInt(kpi.sales).toFixed(0))}
                            </TableCell>

                            <TableCell sx={this.bodyCellSx("right")}>
                              {thousandsSeparator(parseInt(kpi.target).toFixed(0))}
                            </TableCell>

                            <TableCell sx={this.bodyCellSx("center", true)}>
                              {kpi.target > 0 ? parseFloat(kpi.sales / kpi.target * 100).toFixed(1) : parseFloat(0).toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <OverviewDailySalesChart location={this.state.location} />
            </Grid>
          </Grid>
        </Box>
      </Card>
    );
  }

  headerCellSx(align) {
    return {
      padding: {
        xs: "9px 7px",
        sm: "8px 7px"
      },
      textAlign: align,
      whiteSpace: "nowrap",
      fontSize: {
        xs: "13px",
        sm: "13px",
        md: "14px"
      },
      backgroundColor: "#f6f4ef"
    };
  }

  bodyCellSx(align, strong = false) {
    return {
      padding: {
        xs: "9px 7px",
        sm: "8px 7px"
      },
      textAlign: align,
      whiteSpace: "nowrap",
      fontSize: {
        xs: "13.5px",
        sm: "13px",
        md: "14px"
      },
      fontWeight: strong ? 600 : 400
    };
  }
}

export default OverviewSales;
